/**
 * Module IMPACT IMMEUBLES – Moteur de Patrimoine
 *
 * Gestion d'immeubles locatifs (Québec) : fiche immeuble + portes (876/878),
 * baux/TAL, charges, réparations, et moteur financier (NOI, cash-flow, cap rate,
 * DSCR, équité, LTV) avec indicateur garder / optimiser / refinancer / vendre.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Buildings, FloppyDisk, Plus, Trash, Warning, ChartLineUp, House, Wrench, Target, Gauge, TrendUp, Bank, Scales, Gift, Copy, ArrowsClockwise, Lightning, X, EnvelopeSimple, FolderSimple, Sparkle, PaperPlaneTilt, FilePdf, UploadSimple, Eye, Receipt, Calculator, LinkSimple, Paperclip, FileText, ShieldCheck, CheckCircle, ArrowSquareOut, Handshake, ClockCounterClockwise, Lock } from "@phosphor-icons/react";
import BauxPanel from "../components/immeubles/BauxPanel";

const money = (x) => (x === null || x === undefined ? "—" : `${Number(x).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`);
const pct = (x) => (x === null || x === undefined ? "—" : `${Number(x).toFixed(2)} %`);
const ratio = (x) => (x === null || x === undefined ? "—" : Number(x).toFixed(2));

const IMM_IDENTITE = [
  ["nom_interne", "Nom interne", "text"], ["adresse", "Adresse", "text"], ["ville", "Ville", "text"],
  ["province", "Province", "text"], ["code_postal", "Code postal", "text"], ["type_immeuble", "Type d'immeuble", "text"],
  ["nombre_portes", "Nombre de portes", "number"], ["annee_achat", "Année d'achat", "number"],
  ["prix_achat", "Prix d'achat ($)", "number"], ["mise_de_fonds", "Mise de fonds ($)", "number"],
  ["frais_notaire_achat", "Frais de notaire ($)", "number"], ["travaux_achat", "Travaux à l'achat ($)", "number"],
  ["valeur_marche", "Valeur marchande estimée ($)", "number"],
];
const IMM_HYPO = [
  ["institution_financiere", "Institution financière", "text"], ["solde_hypothecaire", "Solde hypothécaire ($)", "number"],
  ["paiement_mensuel_hypo", "Paiement mensuel ($)", "number"], ["taux_interet", "Taux d'intérêt (%)", "number"],
  ["type_taux", "Type de taux (fixe/variable)", "text"], ["amortissement_total", "Amortissement total (ans)", "number"],
  ["amortissement_restant", "Amortissement restant (ans)", "number"], ["date_renouvellement_hypo", "Date de renouvellement", "text"],
  ["penalite_refinancement", "Pénalité si refinancement ($)", "number"], ["penalite_vente", "Pénalité si vente ($)", "number"],
  ["marge_credit_liee", "Marge de crédit liée (oui/non)", "text"], ["marge_credit_solde", "Solde marge ($)", "number"],
];
const IMM_CHARGES = [
  ["taxes_municipales", "Taxes municipales (annuel $)", "number"], ["taxes_scolaires", "Taxes scolaires (annuel $)", "number"],
  ["assurance", "Assurance (annuel $)", "number"], ["hydro_mensuel", "Hydro payée (mensuel $)", "number"],
  ["chauffage_mensuel", "Chauffage payé (mensuel $)", "number"], ["internet_mensuel", "Internet payé (mensuel $)", "number"],
  ["deneigement", "Déneigement (annuel $)", "number"], ["entretien_terrain", "Entretien terrain (annuel $)", "number"],
  ["menage", "Ménage (annuel $)", "number"], ["frais_bancaires", "Frais bancaires/admin (annuel $)", "number"],
  ["honoraires", "Honoraires pros (annuel $)", "number"], ["autres_charges", "Autres charges (annuel $)", "number"],
  ["reserve_entretien", "Réserve entretien (annuel $)", "number"], ["reserve_capex", "Réserve CapEx (annuel $)", "number"],
];
const IMM_REVENUS = [
  ["autres_revenus", "Autres revenus (annuel $)", "number"], ["mois_vacants", "Mois vacants (12 mois)", "number"],
  ["pertes_loyers", "Pertes de loyers (annuel $)", "number"], ["encaisse_12mois", "Encaissé réel sur 12 mois ($)", "number"],
  ["paiements_organisme", "Paiements via organisme (oui/non)", "text"], ["paiements_organisme_details", "Détails organisme", "text"],
];
const IMM_STRATEGIE = [
  ["valeur_apres_optimisation", "Valeur après optimisation ($)", "number"],
  ["montant_refinancable", "Montant refinançable estimé ($)", "number"],
  ["commission_vente", "Commission si vente ($)", "number"], ["couts_sortie", "Coûts de sortie ($)", "number"],
  ["dette_a_rembourser", "Dette à rembourser via refi ($)", "number"],
  ["mise_de_fonds_prochaine", "Mise de fonds prochaine porte ($)", "number"],
  ["travaux_avant_refi", "Travaux majeurs avant refinancement", "text"],
];
const PRIORITES = [
  ["probleme_1", "Problème #1", "text"], ["probleme_2", "Problème #2", "text"], ["probleme_3", "Problème #3", "text"],
  ["objectif_1", "Objectif #1", "text"], ["objectif_2", "Objectif #2", "text"], ["objectif_3", "Objectif #3", "text"],
];

const PORTE_SECTIONS = [
  ["Identité & loyer", [
    ["identifiant", "Identifiant", "text"], ["adresse", "Adresse", "text"], ["nombre_pieces", "Nombre de pièces", "number"],
    ["loyer_actuel", "Loyer mensuel actuel ($)", "number"], ["loyer_marche", "Loyer de marché estimé ($)", "number"],
    ["date_dernier_ajustement", "Dernier ajustement", "text"], ["mode_paiement", "Mode de paiement", "text"],
  ]],
  ["Services inclus", [
    ["stationnement_inclus", "Stationnement", "text"], ["rangement_inclus", "Rangement", "text"],
    ["hydro_incluse", "Hydro incluse", "text"], ["chauffage_inclus", "Chauffage inclus", "text"],
    ["internet_inclus", "Internet inclus", "text"], ["meuble", "Meublé", "text"],
  ]],
  ["Locataire", [
    ["locataire_nom", "Locataire / organisme", "text"], ["contact_principal", "Contact principal", "text"],
    ["telephone", "Téléphone", "text"], ["courriel", "Courriel", "text"], ["type_locataire", "Type (particulier/organisme)", "text"],
  ]],
  ["Bail & état", [
    ["date_debut_bail", "Début du bail", "text"], ["date_fin_bail", "Fin du bail", "text"], ["duree_bail", "Durée", "text"],
    ["retards", "Retards (oui/non)", "text"], ["retards_details", "Détails retards", "text"],
    ["etat_general", "État général", "text"], ["problemes_connus", "Problèmes connus", "text"],
    ["travaux_a_prevoir", "Travaux à prévoir", "text"], ["photos_dispo", "Photos dispo (oui/non)", "text"],
  ]],
  ["Avis / TAL", [
    ["avis_fenetre_ouvre", "Fenêtre d'avis ouvre", "text"], ["avis_date_limite", "Date limite d'envoi", "text"],
    ["avis_envoye", "Avis envoyé (oui/non)", "text"], ["avis_date_envoi", "Date réelle d'envoi", "text"],
    ["preuve_reception", "Preuve de réception (oui/non)", "text"], ["date_limite_reponse", "Date limite réponse", "text"],
    ["reponse_recue", "Réponse reçue (oui/non)", "text"], ["acceptation", "Acceptation (oui/non)", "text"],
    ["refus", "Refus (oui/non)", "text"], ["suivi_tal", "Suivi TAL requis (oui/non)", "text"],
  ]],
];

function Field({ cfg, value, onChange }) {
  const [key, label, type] = cfg;
  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1 block">{label}</label>
      <input
        type={type === "number" ? "number" : "text"}
        value={value ?? ""}
        onChange={(e) => onChange(key, e.target.value)}
        data-testid={`imm-field-${key}`}
        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:border-amber-500 outline-none"
      />
    </div>
  );
}

function Section({ title, fields, form, set }) {
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-widest">{title}</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {fields.map((cfg) => <Field key={cfg[0]} cfg={cfg} value={form[cfg[0]]} onChange={set} />)}
      </div>
    </div>
  );
}

export default function ImmeublesModule() {
  const [tab, setTab] = useState("apercu");
  const [immeubles, setImmeubles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  async function loadList() {
    try {
      const r = await api.get("/immeubles");
      setImmeubles(r.data.immeubles || []);
      if (!selectedId && r.data.immeubles?.length) setSelectedId(r.data.immeubles[0].id);
    } catch {
      toast.error("Erreur de chargement des immeubles");
    }
    setLoading(false);
  }
  async function loadDetail(id) {
    if (!id) return;
    try {
      const r = await api.get(`/immeubles/${id}`);
      setDetail(r.data);
    } catch {
      toast.error("Erreur de chargement de la fiche");
    }
  }

  useEffect(() => { loadList(); }, []);
  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId]);

  async function createImmeuble() {
    const nom = window.prompt("Nom interne du nouvel immeuble :", "");
    if (nom === null) return;
    if (!nom.trim()) return toast.error("Nom requis");
    try {
      const r = await api.post("/immeubles", { nom_interne: nom.trim() });
      toast.success("Immeuble créé");
      await loadList();
      setSelectedId(r.data.immeuble.id);
      setTab("immeuble");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  }

  if (loading) return <div className="p-8 text-zinc-500">Chargement…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="immeubles-module">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-zinc-100 flex items-center gap-2">
            <Buildings size={30} weight="fill" className="text-amber-500" /> Impact Immeubles
          </h1>
          <p className="text-sm text-zinc-400 mt-1">Moteur de patrimoine — rentabilité, baux, stratégie garder/vendre</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/immeubles/dossier-brebeuf" data-testid="immeubles-dossier-brebeuf-link" className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 px-3 py-2 text-sm text-amber-200 font-semibold"><Lock weight="fill" /> Dossier Brébeuf (privé)</Link>
          {immeubles.length > 1 && (
            <select
              value={selectedId || ""}
              onChange={(e) => setSelectedId(e.target.value)}
              data-testid="immeuble-select"
              className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100"
            >
              {immeubles.map((i) => <option key={i.id} value={i.id}>{i.nom_interne}</option>)}
            </select>
          )}
          <button onClick={createImmeuble} data-testid="new-immeuble-btn"
            className="bg-zinc-800 hover:bg-zinc-700 text-amber-400 border border-amber-500/30 font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5 text-sm">
            <Plus size={16} weight="bold" /> Nouvel immeuble
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-zinc-800 flex-wrap">
        {[
          { id: "apercu", label: "Vue d'ensemble", icon: ChartLineUp },
          { id: "immeuble", label: "Immeuble", icon: Buildings },
          { id: "portes", label: "Portes", icon: House },
          { id: "croissance", label: "Croissance loyers", icon: TrendUp },
          { id: "depenses", label: "Dépenses & TAL", icon: Calculator },
          { id: "refinancement", label: "Refinancement", icon: Bank },
          { id: "dossier_refi", label: "Dossier refi", icon: ShieldCheck },
          { id: "decision", label: "Garder vs Vendre", icon: Scales },
          { id: "courriels", label: "Courriels & Avis", icon: EnvelopeSimple },
          { id: "baux", label: "Baux & Signature", icon: FileText },
          { id: "documents", label: "Documents", icon: FolderSimple },
          { id: "reparations", label: "Réparations", icon: Wrench },
          { id: "subventions", label: "Subventions", icon: Gift },
          { id: "strategie", label: "Stratégie", icon: Target },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`imm-tab-${t.id}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
                tab === t.id ? "border-amber-500 text-amber-400" : "border-transparent text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <Icon size={16} weight={tab === t.id ? "fill" : "regular"} /> {t.label}
            </button>
          );
        })}
      </div>

      {!detail ? (
        <div className="text-zinc-500 py-10">Chargement de la fiche…</div>
      ) : (
        <>
          {tab === "apercu" && <Apercu analyse={detail.analyse} portes={detail.portes} immeubleId={selectedId} />}
          {tab === "immeuble" && <ImmeubleForm immeuble={detail.immeuble} onSaved={() => loadDetail(selectedId)} sections={[
            ["Identité", IMM_IDENTITE], ["Hypothèque", IMM_HYPO], ["Charges", IMM_CHARGES], ["Revenus", IMM_REVENUS],
          ]} />}
          {tab === "portes" && <PortesPanel immeubleId={selectedId} portes={detail.portes} onReload={() => loadDetail(selectedId)} />}
          {tab === "croissance" && <CroissancePanel immeubleId={selectedId} immeuble={detail.immeuble} />}
          {tab === "depenses" && <DepensesPanel immeubleId={selectedId} portes={detail.portes} />}
          {tab === "refinancement" && <RefinancementPanel immeubleId={selectedId} />}
          {tab === "dossier_refi" && <RefiDossierPanel immeubleId={selectedId} />}
          {tab === "decision" && <DecisionPanel immeubleId={selectedId} />}
          {tab === "courriels" && <CourrielsPanel immeubleId={selectedId} immeuble={detail.immeuble} portes={detail.portes} />}
          {tab === "baux" && <BauxPanel immeubleId={selectedId} portes={detail.portes} />}
          {tab === "documents" && <DocumentsPanel immeubleId={selectedId} portes={detail.portes} />}
          {tab === "reparations" && <ReparationsPanel immeubleId={selectedId} reparations={detail.reparations} onReload={() => loadDetail(selectedId)} />}
          {tab === "subventions" && <SubventionsPanel />}
          {tab === "strategie" && <StrategiePanel immeuble={detail.immeuble} analyse={detail.analyse} onSaved={() => loadDetail(selectedId)} />}
        </>
      )}
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="imm-modal">
      <div className={`bg-zinc-950 border border-zinc-800 rounded-2xl p-6 w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[85vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-zinc-100">{title}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200" data-testid="modal-close"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Apercu({ analyse: a, portes, immeubleId }) {
  const [intel, setIntel] = useState(null);
  const [loadingIntel, setLoadingIntel] = useState(false);

  async function runIntelligence() {
    setLoadingIntel(true);
    try {
      const r = await api.post(`/immeubles/${immeubleId}/intelligence`);
      setIntel(r.data);
      toast.success(r.data.resume);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
    setLoadingIntel(false);
  }

  const decisionColor = {
    "Garder": "text-green-400 border-green-500/40 bg-green-500/10",
    "Optimiser puis garder": "text-amber-400 border-amber-500/40 bg-amber-500/10",
    "Refinancer et construire": "text-blue-400 border-blue-500/40 bg-blue-500/10",
    "Réviser / envisager la vente": "text-red-400 border-red-500/40 bg-red-500/10",
    "À compléter": "text-zinc-400 border-zinc-700 bg-zinc-800/40",
  }[a.decision] || "text-zinc-400 border-zinc-700 bg-zinc-800/40";

  const cards = [
    { label: "Cash-flow mensuel", value: money(a.cashflow_monthly), color: a.cashflow_monthly >= 0 ? "text-green-400" : "text-red-400", testid: "kpi-cashflow" },
    { label: "NOI annuel", value: money(a.noi), color: "text-zinc-100", testid: "kpi-noi" },
    { label: "Loyers bruts / mois", value: money(a.gross_monthly), color: "text-zinc-100", testid: "kpi-gross" },
    { label: "Cap rate", value: pct(a.cap_rate), color: "text-amber-400", testid: "kpi-caprate" },
    { label: "DSCR", value: ratio(a.dscr), color: (a.dscr != null && a.dscr < 1) ? "text-red-400" : "text-green-400", testid: "kpi-dscr" },
    { label: "Équité estimée", value: money(a.equity), color: "text-blue-400", testid: "kpi-equity" },
    { label: "LTV", value: pct(a.ltv), color: "text-zinc-100", testid: "kpi-ltv" },
    { label: "Écart de loyer / mois", value: money(a.rent_gap_monthly), color: a.rent_gap_monthly > 0 ? "text-amber-400" : "text-zinc-100", testid: "kpi-rentgap" },
    { label: "Service de la dette / an", value: money(a.debt_service), color: "text-zinc-100", testid: "kpi-debt" },
    { label: "Dépenses d'exploitation / an", value: money(a.opex_annual), color: "text-zinc-100", testid: "kpi-opex" },
  ];

  return (
    <div className="space-y-6" data-testid="immeuble-apercu">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className={`flex-1 min-w-[280px] rounded-xl border p-5 ${decisionColor}`} data-testid="decision-banner">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80">
            <Gauge size={16} weight="fill" /> Recommandation stratégique
          </div>
          <div className="text-2xl font-bold mt-1">{a.decision}</div>
          <div className="text-sm opacity-90 mt-1">{a.decision_note}</div>
        </div>
        <button onClick={runIntelligence} disabled={loadingIntel} data-testid="intelligence-btn"
          className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-semibold px-4 py-3 rounded-xl flex items-center gap-2 text-sm self-stretch">
          <Lightning size={18} weight="fill" /> {loadingIntel ? "Analyse…" : "Mettre à jour l'intelligence immeuble"}
        </button>
      </div>

      {intel && (
        <Modal title="Intelligence immeuble — actions prioritaires" onClose={() => setIntel(null)} wide>
          <div className="space-y-3">
            <div className="text-sm text-zinc-300 bg-zinc-900 rounded-lg p-3">{intel.resume}</div>
            {intel.actions.length === 0 ? (
              <div className="text-green-400 text-sm">Aucune action prioritaire — tout est à jour. 🎉</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-zinc-500 border-b border-zinc-800">
                  <th className="py-2 px-2">Priorité</th><th className="py-2 px-2">Porte</th><th className="py-2 px-2">Action</th>
                </tr></thead>
                <tbody>
                  {intel.actions.map((ac, i) => (
                    <tr key={i} className="border-b border-zinc-900" data-testid={`intel-action-${i}`}>
                      <td className="py-2 px-2"><span className={`text-xs px-2 py-0.5 rounded ${ac.priorite === "Haute" ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>{ac.priorite}</span></td>
                      <td className="py-2 px-2 text-zinc-400">{ac.porte}</td>
                      <td className="py-2 px-2 text-zinc-200">{ac.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Modal>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4" data-testid={c.testid}>
            <div className="text-xs text-zinc-500">{c.label}</div>
            <div className={`text-xl font-bold mt-1 ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {a.alerts?.length > 0 && (
        <div className="space-y-2" data-testid="alerts-section">
          <h3 className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
            <Warning size={18} weight="fill" className="text-amber-500" /> Alertes ({a.alerts.length})
          </h3>
          <ul className="space-y-1.5">
            {a.alerts.map((al, i) => (
              <li key={i} className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">{al}</li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-zinc-300 mb-2">Rentabilité par porte</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-zinc-800">
                <th className="py-2 px-3">Porte</th>
                <th className="py-2 px-3 text-right">Loyer actuel</th>
                <th className="py-2 px-3 text-right">Loyer marché</th>
                <th className="py-2 px-3 text-right">Écart</th>
              </tr>
            </thead>
            <tbody>
              {portes.map((p) => {
                const gap = Math.max(Number(p.loyer_marche || 0) - Number(p.loyer_actuel || 0), 0);
                return (
                  <tr key={p.id} className="border-b border-zinc-900" data-testid={`apercu-porte-${p.identifiant}`}>
                    <td className="py-2 px-3 text-zinc-200">{p.identifiant}</td>
                    <td className="py-2 px-3 text-right text-zinc-300">{money(p.loyer_actuel || 0)}</td>
                    <td className="py-2 px-3 text-right text-zinc-400">{p.loyer_marche ? money(p.loyer_marche) : "—"}</td>
                    <td className={`py-2 px-3 text-right ${gap > 0 ? "text-amber-400" : "text-zinc-500"}`}>{gap > 0 ? `+${money(gap)}` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ImmeubleForm({ immeuble, sections, onSaved }) {
  const [form, setForm] = useState(immeuble);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(immeuble); }, [immeuble]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      await api.put(`/immeubles/${immeuble.id}`, form);
      toast.success("Fiche immeuble enregistrée — rentabilité recalculée");
      onSaved();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  }

  return (
    <div className="space-y-6" data-testid="immeuble-form">
      {sections.map(([title, fields]) => (
        <Section key={title} title={title} fields={fields} form={form} set={set} />
      ))}
      <button onClick={save} disabled={saving} data-testid="save-immeuble-btn"
        className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-semibold px-5 py-2 rounded-lg flex items-center gap-2">
        <FloppyDisk size={18} weight="fill" /> {saving ? "Enregistrement…" : "Enregistrer la fiche"}
      </button>
    </div>
  );
}

function PortesPanel({ immeubleId, portes, onReload }) {
  async function addPorte() {
    try {
      await api.post(`/immeubles/${immeubleId}/portes`, { identifiant: "Nouvelle porte" });
      toast.success("Porte ajoutée");
      onReload();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  }
  return (
    <div className="space-y-5" data-testid="portes-panel">
      <button onClick={addPorte} data-testid="add-porte-btn"
        className="bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
        <Plus size={16} weight="bold" /> Ajouter une porte
      </button>
      {portes.map((p) => <PorteCard key={p.id} porte={p} onReload={onReload} />)}
    </div>
  );
}

function PorteCard({ porte, onReload }) {
  const [form, setForm] = useState(porte);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(porte); }, [porte]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      await api.put(`/immeubles/portes/${porte.id}`, form);
      toast.success(`Porte ${form.identifiant || ""} enregistrée`);
      onReload();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  }
  async function del() {
    if (!window.confirm("Supprimer cette porte ?")) return;
    try {
      await api.delete(`/immeubles/portes/${porte.id}`);
      toast.success("Porte supprimée");
      onReload();
    } catch {
      toast.error("Erreur de suppression");
    }
  }

  return (
    <div className="border border-zinc-800 rounded-xl p-4 space-y-4 bg-zinc-900/30" data-testid={`porte-card-${porte.identifiant}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-amber-400 flex items-center gap-2">
          <House size={20} weight="fill" /> Porte {form.identifiant}
        </h3>
        <button onClick={del} className="text-zinc-500 hover:text-red-400" data-testid={`del-porte-${porte.identifiant}`}>
          <Trash size={18} />
        </button>
      </div>
      {PORTE_SECTIONS.map(([title, fields]) => (
        <Section key={title} title={title} fields={fields} form={form} set={set} />
      ))}
      <button onClick={save} disabled={saving} data-testid={`save-porte-${porte.identifiant}`}
        className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
        <FloppyDisk size={16} weight="fill" /> {saving ? "Enregistrement…" : "Enregistrer la porte"}
      </button>
    </div>
  );
}

function ReparationsPanel({ immeubleId, reparations, onReload }) {
  const [form, setForm] = useState({ date: "", porte: "", description: "", cause: "", responsable: "", cout_estime: "", cout_reel: "", statut: "Nouveau" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function add() {
    if (!form.description.trim()) return toast.error("Description requise");
    try {
      await api.post(`/immeubles/${immeubleId}/reparations`, form);
      toast.success("Réparation ajoutée");
      setForm({ date: "", porte: "", description: "", cause: "", responsable: "", cout_estime: "", cout_reel: "", statut: "Nouveau" });
      onReload();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  }
  async function del(id) {
    if (!window.confirm("Supprimer cette réparation ?")) return;
    try {
      await api.delete(`/immeubles/reparations/${id}`);
      toast.success("Supprimée");
      onReload();
    } catch {
      toast.error("Erreur de suppression");
    }
  }

  const F = (k, label, type = "text") => (
    <div>
      <label className="text-xs text-zinc-400 mb-1 block">{label}</label>
      <input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`rep-field-${k}`}
        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="reparations-panel">
      <div className="border border-zinc-800 rounded-xl p-4 space-y-3 bg-zinc-900/30">
        <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-widest">Nouvelle réparation</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {F("date", "Date", "date")}{F("porte", "Porte")}{F("cout_estime", "Coût estimé ($)", "number")}{F("cout_reel", "Coût réel ($)", "number")}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {F("description", "Description")}{F("cause", "Cause")}{F("responsable", "Responsable")}
        </div>
        <button onClick={add} data-testid="add-reparation-btn"
          className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
          <Plus size={16} weight="bold" /> Ajouter
        </button>
      </div>

      {reparations.length === 0 ? (
        <div className="text-center py-8 text-zinc-500 text-sm">Aucune réparation enregistrée.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="reparations-list">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-zinc-800">
                <th className="py-2 px-3">Date</th><th className="py-2 px-3">Porte</th><th className="py-2 px-3">Description</th>
                <th className="py-2 px-3 text-right">Coût réel</th><th className="py-2 px-3">Statut</th><th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {reparations.map((r) => (
                <tr key={r.id} className="border-b border-zinc-900" data-testid={`reparation-row-${r.id}`}>
                  <td className="py-2 px-3 text-zinc-400">{r.date || "—"}</td>
                  <td className="py-2 px-3 text-zinc-300">{r.porte || "—"}</td>
                  <td className="py-2 px-3 text-zinc-200">{r.description}</td>
                  <td className="py-2 px-3 text-right text-zinc-300">{r.cout_reel ? money(r.cout_reel) : "—"}</td>
                  <td className="py-2 px-3 text-zinc-400">{r.statut}</td>
                  <td className="py-2 px-3 text-right">
                    <button onClick={() => del(r.id)} className="text-zinc-500 hover:text-red-400"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StrategiePanel({ immeuble, analyse, onSaved }) {
  const [form, setForm] = useState(immeuble);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(immeuble); }, [immeuble]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      await api.put(`/immeubles/${immeuble.id}`, form);
      toast.success("Stratégie enregistrée");
      onSaved();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  }

  return (
    <div className="space-y-6" data-testid="strategie-panel">
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4">
        <div className="text-xs text-zinc-500 uppercase tracking-widest">Recommandation actuelle du moteur</div>
        <div className="text-xl font-bold text-amber-400 mt-1">{analyse.decision}</div>
        <div className="text-sm text-zinc-400">{analyse.decision_note}</div>
      </div>

      <div className="space-y-3">
        <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-widest">Décision</h3>
        <div className="max-w-xs">
          <label className="text-xs text-zinc-400 mb-1 block">Stratégie choisie</label>
          <select value={form.strategie || ""} onChange={(e) => set("strategie", e.target.value)} data-testid="imm-field-strategie"
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100">
            <option value="">—</option>
            <option value="garder">Garder</option>
            <option value="optimiser">Optimiser</option>
            <option value="refinancer">Refinancer</option>
            <option value="vendre">Vendre</option>
          </select>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Field cfg={["strategie_pourquoi", "Pourquoi cette stratégie", "text"]} value={form.strategie_pourquoi} onChange={set} />
        </div>
      </div>

      <Section title="Valeur & refinancement" fields={IMM_STRATEGIE} form={form} set={set} />
      <Section title="Priorités (3 problèmes · 3 objectifs)" fields={PRIORITES} form={form} set={set} />

      <button onClick={save} disabled={saving} data-testid="save-strategie-btn"
        className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-semibold px-5 py-2 rounded-lg flex items-center gap-2">
        <FloppyDisk size={18} weight="fill" /> {saving ? "Enregistrement…" : "Enregistrer la stratégie"}
      </button>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// CROISSANCE DES LOYERS
// ════════════════════════════════════════════════════════════════════════════
const RISK_COLOR = { "Faible": "text-green-400", "Modéré": "text-amber-400", "Élevé": "text-red-400" };
const NOTICE_COLOR = {
  info: "bg-zinc-800/60 text-zinc-300 border-zinc-700",
  warning: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  critical: "bg-red-500/15 text-red-300 border-red-500/30",
};

function CroissancePanel({ immeubleId, immeuble }) {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null); // {type:'avis'|'courriel', porte}

  useEffect(() => {
    api.get(`/immeubles/${immeubleId}/croissance`).then((r) => setData(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, [immeubleId]);

  if (!data) return <div className="text-zinc-500 py-8">Chargement…</div>;

  return (
    <div className="space-y-5" data-testid="croissance-panel">
      <p className="text-xs text-zinc-500">
        Les scénarios sont indicatifs. Au Québec, l'avis de modification se transmet 3 à 6 mois avant la fin du bail ;
        le locataire a 1 mois pour répondre. Le courriel ne remplace pas l'avis officiel du TAL.
      </p>
      {data.portes.length === 0 && <div className="text-zinc-500 text-sm">Aucune porte avec loyer saisi.</div>}
      {data.portes.map((p) => (
        <div key={p.porte_id} className="border border-zinc-800 rounded-xl p-4 space-y-3 bg-zinc-900/30" data-testid={`croissance-porte-${p.identifiant}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-lg font-bold text-amber-400">Porte {p.identifiant}</h3>
            <div className="text-sm text-zinc-400">
              Actuel <span className="text-zinc-200">{money(p.loyer_actuel)}</span>
              {p.loyer_marche ? <> · Marché <span className="text-zinc-200">{money(p.loyer_marche)}</span> · Écart <span className="text-amber-400">+{money(p.ecart)}</span></> : <span className="text-zinc-600"> · loyer de marché non saisi</span>}
            </div>
          </div>

          {p.avis && (
            <div className={`text-xs rounded-lg border px-3 py-2 ${NOTICE_COLOR[p.avis.ton]}`} data-testid={`avis-badge-${p.identifiant}`}>
              <b>Avis de renouvellement :</b> {p.avis.statut} · Fenêtre {p.avis.fenetre_ouvre} → limite {p.avis.date_limite_avis}
              {p.avis.jours_avant_limite >= 0 ? ` (J-${p.avis.jours_avant_limite})` : ` (dépassé de ${-p.avis.jours_avant_limite} j)`}
            </div>
          )}

          {p.scenarios.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-zinc-500 border-b border-zinc-800">
                  <th className="py-1.5 px-2">Scénario</th><th className="py-1.5 px-2 text-right">Nouveau loyer</th>
                  <th className="py-1.5 px-2 text-right">+/mois</th><th className="py-1.5 px-2 text-right">+/an</th>
                  <th className="py-1.5 px-2 text-right">Effet valeur</th><th className="py-1.5 px-2">Risque relation</th><th className="py-1.5 px-2">Risque légal</th>
                </tr></thead>
                <tbody>
                  {p.scenarios.map((s) => (
                    <tr key={s.key} className="border-b border-zinc-900" data-testid={`scenario-${p.identifiant}-${s.key}`}>
                      <td className="py-1.5 px-2 text-zinc-200">{s.label}</td>
                      <td className="py-1.5 px-2 text-right text-amber-400 font-semibold">{money(s.new_rent)}</td>
                      <td className="py-1.5 px-2 text-right text-zinc-300">+{money(s.increase_monthly)}</td>
                      <td className="py-1.5 px-2 text-right text-zinc-400">+{money(s.increase_annual)}</td>
                      <td className="py-1.5 px-2 text-right text-blue-400">{s.value_effect ? `+${money(s.value_effect)}` : "—"}</td>
                      <td className={`py-1.5 px-2 ${RISK_COLOR[s.relationship_risk]}`}>{s.relationship_risk}</td>
                      <td className={`py-1.5 px-2 ${RISK_COLOR[s.legal_risk]}`}>{s.legal_risk}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="text-zinc-600 text-sm">Saisis le loyer actuel de cette porte pour voir les scénarios.</div>}

          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setModal({ type: "avis", porte: p })} data-testid={`prep-avis-btn-${p.identifiant}`}
              className="bg-zinc-800 hover:bg-zinc-700 text-amber-400 border border-amber-500/30 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5">
              <Scales size={15} weight="fill" /> Préparer avis TAL
            </button>
            <button onClick={() => setModal({ type: "courriel", porte: p })} data-testid={`gen-courriel-btn-${p.identifiant}`}
              className="bg-zinc-800 hover:bg-zinc-700 text-amber-400 border border-amber-500/30 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5">
              <Copy size={15} weight="fill" /> Générer courriel d'accompagnement
            </button>
          </div>
        </div>
      ))}

      {modal?.type === "avis" && <AvisModal porte={modal.porte} onClose={() => setModal(null)} />}
      {modal?.type === "courriel" && <CourrielModal porte={modal.porte} immeuble={immeuble} onClose={() => setModal(null)} />}
    </div>
  );
}

function AvisModal({ porte, onClose }) {
  const a = porte.avis;
  return (
    <Modal title={`Préparer l'avis TAL — Porte ${porte.identifiant}`} onClose={onClose} wide>
      <div className="space-y-3 text-sm text-zinc-300">
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-amber-200">
          ⚠️ Ce module <b>accompagne</b> la démarche, il ne remplace pas le formulaire officiel du TAL.
        </div>
        {a ? (
          <ul className="space-y-1.5 list-disc pl-5">
            <li>Fin du bail : <b>{a.fin_bail}</b></li>
            <li>Fenêtre d'avis ouvre : <b>{a.fenetre_ouvre}</b></li>
            <li>Date limite d'envoi recommandée : <b>{a.date_limite_avis}</b> {a.jours_avant_limite >= 0 ? `(J-${a.jours_avant_limite})` : `(dépassée)`}</li>
            <li>{a.note_reponse}</li>
          </ul>
        ) : <div className="text-zinc-500">Saisis la date de fin de bail de cette porte pour calculer les échéances.</div>}
        <div className="font-semibold text-zinc-200 pt-2">À inclure dans l'avis officiel :</div>
        <ul className="space-y-1 list-disc pl-5 text-zinc-400">
          <li>Loyer actuel et nouveau loyer proposé</li>
          <li>Date d'entrée en vigueur (renouvellement)</li>
          <li>Toute modification d'une autre condition du bail</li>
          <li>Délai d'un mois pour la réponse du locataire</li>
          <li>Conservez une <b>preuve de réception</b> (courriel + accusé, ou recommandé)</li>
        </ul>
        <a href="https://www.tal.gouv.qc.ca/fr/modeles-d-avis/trouver-un-modele-d-avis" target="_blank" rel="noreferrer"
          className="inline-block text-amber-400 hover:text-amber-300 underline text-sm">Ouvrir les modèles d'avis officiels du TAL ↗</a>
      </div>
    </Modal>
  );
}

function CourrielModal({ porte, immeuble, onClose }) {
  const [scKey, setScKey] = useState(porte.scenarios[1]?.key || porte.scenarios[0]?.key || "");
  const sc = porte.scenarios.find((s) => s.key === scKey);
  const adresse = porte.identifiant ? `${porte.identifiant} ${(immeuble.adresse || "").replace(/^\d+-?\d*\s*/, "")}`.trim() : (immeuble.adresse || "");
  const dateRenouv = immeuble.date_renouvellement_hypo || porte.avis?.fin_bail || "[date de renouvellement]";

  const texte = sc ? `Objet : Avis de modification de votre bail — ${adresse}

Bonjour ${porte.locataire_nom || "[locataire]"},

Par la présente, je vous transmets un avis concernant la modification de votre bail pour le logement situé au ${adresse}, à compter du ${dateRenouv}.

Le loyer mensuel passerait de ${money(porte.loyer_actuel)} à ${money(sc.new_rent)}, soit une augmentation de ${money(sc.increase_monthly)} par mois.

Veuillez prendre connaissance de l'avis officiel ci-joint, qui détaille les conditions. Conformément à la réglementation québécoise, vous disposez d'un délai d'un (1) mois suivant la réception de cet avis pour me faire part de votre réponse.

Je demeure disponible pour toute question et vous remercie de votre collaboration.

Cordialement,
[Votre nom]
Propriétaire` : "";

  return (
    <Modal title={`Courriel d'accompagnement — Porte ${porte.identifiant}`} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-zinc-400">Scénario :</span>
          <select value={scKey} onChange={(e) => setScKey(e.target.value)} data-testid="courriel-scenario-select"
            className="bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1 text-zinc-100 text-sm">
            {porte.scenarios.map((s) => <option key={s.key} value={s.key}>{s.label} → {money(s.new_rent)}</option>)}
          </select>
        </div>
        <textarea readOnly value={texte} data-testid="courriel-text" rows={15}
          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 font-mono" />
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-xs text-zinc-500">💡 La rédaction IA personnalisée (Claude) arrive en Phase 2B.</span>
          <button onClick={() => { navigator.clipboard.writeText(texte); toast.success("Courriel copié !"); }} data-testid="copy-courriel-btn"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
            <Copy size={16} weight="fill" /> Copier le courriel
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// REFINANCEMENT
// ════════════════════════════════════════════════════════════════════════════
function RefinancementPanel({ immeubleId }) {
  const [targetLtv, setTargetLtv] = useState(80);
  const [data, setData] = useState(null);

  async function load(ltv) {
    try {
      const r = await api.get(`/immeubles/${immeubleId}/refinancement?target_ltv=${ltv}`);
      setData(r.data);
    } catch { toast.error("Erreur de chargement"); }
  }
  useEffect(() => { load(targetLtv); /* eslint-disable-next-line */ }, [immeubleId]);

  if (!data) return <div className="text-zinc-500 py-8">Chargement…</div>;

  const cards = [
    { label: "Équité estimée", value: money(data.equity), color: "text-blue-400" },
    { label: "LTV actuel", value: pct(data.ltv_actuel), color: "text-zinc-100" },
    { label: `Financement max (${data.target_ltv}%)`, value: money(data.max_financing), color: "text-zinc-100" },
    { label: "Cash-out disponible", value: money(data.cash_out), color: "text-green-400" },
    { label: "DSCR", value: ratio(data.dscr), color: (data.dscr != null && data.dscr < 1) ? "text-red-400" : "text-green-400" },
    { label: "Mise de fonds prochaine porte", value: money(data.mise_de_fonds_prochaine), color: "text-amber-400" },
  ];

  return (
    <div className="space-y-5" data-testid="refinancement-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm text-zinc-400">LTV cible (%)</label>
        <input type="number" value={targetLtv} min={50} max={95} data-testid="target-ltv-input"
          onChange={(e) => setTargetLtv(e.target.value)} onBlur={() => load(targetLtv)}
          className="w-24 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" />
        <button onClick={() => load(targetLtv)} data-testid="recalc-refi-btn"
          className="bg-zinc-800 hover:bg-zinc-700 text-amber-400 border border-amber-500/30 px-3 py-2 rounded-lg text-sm">Recalculer</button>
      </div>

      <div className={`rounded-xl border p-5 ${data.cash_out > 1000 ? "border-green-500/40 bg-green-500/10 text-green-300" : "border-zinc-700 bg-zinc-800/40 text-zinc-300"}`} data-testid="refi-reco">
        <div className="text-xs uppercase tracking-widest opacity-80 flex items-center gap-1.5"><Bank size={15} weight="fill" /> Recommandation</div>
        <div className="text-xl font-bold mt-1">{data.recommandation}</div>
        <div className="text-sm opacity-90 mt-1">{data.note}</div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4">
            <div className="text-xs text-zinc-500">{c.label}</div>
            <div className={`text-xl font-bold mt-1 ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// GARDER VS VENDRE
// ════════════════════════════════════════════════════════════════════════════
function DecisionPanel({ immeubleId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/immeubles/${immeubleId}/scenarios`).then((r) => setData(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, [immeubleId]);
  if (!data) return <div className="text-zinc-500 py-8">Chargement…</div>;

  const stars = (n) => "★".repeat(n) + "☆".repeat(5 - n);

  return (
    <div className="space-y-5" data-testid="decision-panel">
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-5" data-testid="decision-reco">
        <div className="text-xs uppercase tracking-widest text-amber-400/80 flex items-center gap-1.5"><Scales size={15} weight="fill" /> Recommandation du moteur</div>
        <div className="text-xl font-bold text-amber-400 mt-1">{data.recommandation}</div>
        <div className="text-sm text-zinc-300 mt-1">{data.note}</div>
        <div className="text-sm text-zinc-400 mt-2">Meilleur scénario calculé : <b className="text-amber-300">{data.meilleur_scenario}</b></div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.scenarios.map((s) => (
          <div key={s.key} className={`rounded-xl border p-4 space-y-1.5 ${s.label === data.meilleur_scenario ? "border-amber-500/50 bg-amber-500/5" : "border-zinc-800 bg-zinc-900/40"}`} data-testid={`scenario-card-${s.key}`}>
            <div className="font-bold text-zinc-100">{s.label}</div>
            <div className="text-amber-400 text-sm">{stars(s.score)}</div>
            {s.net_proceeds !== undefined ? (
              <div className="text-sm text-zinc-300">Produit net de vente : <b className="text-green-400">{money(s.net_proceeds)}</b></div>
            ) : (
              <div className="text-sm text-zinc-300">Cash-flow 12 mois : <b className={s.cashflow_12m >= 0 ? "text-green-400" : "text-red-400"}>{money(s.cashflow_12m)}</b></div>
            )}
            <div className="text-xs text-zinc-500 space-y-0.5 pt-1">
              <div>Gestion : {s.burden}</div>
              <div>Création de valeur : {s.value_creation}</div>
              <div>Liquidité : {s.liquidity}</div>
              <div>Prochaine acquisition : {s.next_acquisition}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SUBVENTIONS
// ════════════════════════════════════════════════════════════════════════════
function SubventionsPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ nom_programme: "", source: "", type_travaux: "", date_limite: "", montant_possible: "", statut: "À vérifier" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function load() {
    try { const r = await api.get("/immeuble-subventions"); setRows(r.data.subventions || []); }
    catch { toast.error("Erreur de chargement"); }
  }
  useEffect(() => { load(); }, []);

  async function refresh() {
    try { const r = await api.post("/immeuble-subventions/refresh"); toast.success(`${r.data.added} programme(s) ajouté(s)`); load(); }
    catch { toast.error("Erreur"); }
  }
  async function add() {
    if (!form.nom_programme.trim()) return toast.error("Nom du programme requis");
    try {
      await api.post("/immeuble-subventions", form);
      toast.success("Programme ajouté");
      setForm({ nom_programme: "", source: "", type_travaux: "", date_limite: "", montant_possible: "", statut: "À vérifier" });
      load();
    } catch (e) { toast.error("Erreur : " + (e?.response?.data?.detail || e.message)); }
  }
  async function del(id) {
    if (!window.confirm("Supprimer ce programme ?")) return;
    try { await api.delete(`/immeuble-subventions/${id}`); toast.success("Supprimé"); load(); }
    catch { toast.error("Erreur"); }
  }

  return (
    <div className="space-y-5" data-testid="subventions-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-zinc-500 max-w-xl">Veille des programmes d'aide Québec/Canada. Vérifie toujours l'admissibilité et les dates limites sur le site officiel du programme.</p>
        <button onClick={refresh} data-testid="refresh-subventions-btn"
          className="bg-zinc-800 hover:bg-zinc-700 text-amber-400 border border-amber-500/30 px-3 py-2 rounded-lg text-sm flex items-center gap-1.5">
          <ArrowsClockwise size={15} weight="bold" /> Actualiser les programmes
        </button>
      </div>

      <div className="border border-zinc-800 rounded-xl p-4 space-y-3 bg-zinc-900/30">
        <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-widest">Ajouter un programme</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[["nom_programme", "Nom du programme"], ["source", "Source (Québec/fédéral/municipal/utilité)"], ["type_travaux", "Type de travaux"], ["date_limite", "Date limite"], ["montant_possible", "Montant possible"], ["statut", "Statut"]].map(([k, l]) => (
            <div key={k}>
              <label className="text-xs text-zinc-400 mb-1 block">{l}</label>
              <input value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`subv-field-${k}`}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" />
            </div>
          ))}
        </div>
        <button onClick={add} data-testid="add-subvention-btn"
          className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
          <Plus size={16} weight="bold" /> Ajouter
        </button>
      </div>

      <div className="space-y-2" data-testid="subventions-list">
        {rows.map((s) => (
          <div key={s.id} className="border border-zinc-800 rounded-xl p-4 flex items-start justify-between gap-3" data-testid={`subvention-row-${s.id}`}>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-zinc-100">{s.nom_programme}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-400">{s.source}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-amber-500/15 text-amber-300">{s.statut}</span>
              </div>
              {s.type_travaux && <div className="text-sm text-zinc-400">{s.type_travaux}</div>}
              {s.admissibilite && <div className="text-xs text-zinc-500">Admissibilité : {s.admissibilite}</div>}
              {(s.date_limite || s.montant_possible) && <div className="text-xs text-zinc-500">{s.date_limite && `Date limite : ${s.date_limite}`} {s.montant_possible && ` · Montant : ${s.montant_possible}`}</div>}
            </div>
            <button onClick={() => del(s.id)} className="text-zinc-500 hover:text-red-400" data-testid={`del-subvention-${s.id}`}><Trash size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// COURRIELS & AVIS (modèles FR + IA Claude + envoi Gmail + ligne du temps)
// ════════════════════════════════════════════════════════════════════════════
const PROOF_COLORS = {
  "envoyé": "bg-blue-500/15 text-blue-300", "preuve téléversée": "bg-amber-500/15 text-amber-300",
  "accusé reçu": "bg-green-500/15 text-green-300", "brouillon": "bg-zinc-700 text-zinc-300",
};
const RESP_COLORS = {
  "en attente": "bg-zinc-700 text-zinc-300", "reçue": "bg-green-500/15 text-green-300",
  "refus": "bg-red-500/15 text-red-300", "acceptation": "bg-green-500/15 text-green-300",
};

function fillTemplate(str, ctx) {
  return (str || "").replace(/\{(\w+)\}/g, (_, k) => ctx[k] || `[${k}]`);
}

function CourrielsPanel({ immeubleId, immeuble, portes }) {
  const [modeles, setModeles] = useState([]);
  const [comms, setComms] = useState([]);
  const [porteId, setPorteId] = useState(portes[0]?.id || "");
  const [tplKey, setTplKey] = useState("");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [nouveauLoyer, setNouveauLoyer] = useState("");
  const [busy, setBusy] = useState(false);

  const porte = portes.find((p) => p.id === porteId);
  const ctx = {
    locataire: porte?.locataire_nom || "[locataire]",
    adresse: porte ? `${porte.identifiant} ${(immeuble.adresse || "").replace(/^\d+-?\d*\s*/, "")}`.trim() : (immeuble.adresse || "[adresse]"),
    loyer_actuel: porte?.loyer_actuel ? money(porte.loyer_actuel) : "[loyer actuel]",
    nouveau_loyer: nouveauLoyer ? money(Number(nouveauLoyer)) : "[nouveau loyer]",
    proprietaire: "Ralph David Jean-Pierre",
  };

  async function loadComms() {
    try { const r = await api.get(`/immeubles/${immeubleId}/communications`); setComms(r.data.communications || []); } catch { /* ignore */ }
  }
  useEffect(() => {
    api.get("/immeubles/courriels/modeles").then((r) => setModeles(r.data.modeles || [])).catch(() => {});
    loadComms();
    if (porte?.courriel) setTo(porte.courriel);
    // eslint-disable-next-line
  }, [immeubleId]);
  useEffect(() => { if (porte?.courriel) setTo(porte.courriel); }, [porteId]);

  function applyTemplate(key) {
    setTplKey(key);
    const m = modeles.find((x) => x.key === key);
    if (m) { setSubject(fillTemplate(m.subject, ctx)); setBody(fillTemplate(m.body, ctx)); }
  }

  async function genererIA() {
    setBusy(true);
    try {
      const r = await api.post("/immeubles/courriels/generer", {
        template_key: tplKey || null, type_label: modeles.find((m) => m.key === tplKey)?.label || "",
        locataire: ctx.locataire, adresse: ctx.adresse, loyer_actuel: ctx.loyer_actuel,
        nouveau_loyer: ctx.nouveau_loyer, proprietaire: ctx.proprietaire,
      });
      setSubject(r.data.subject); setBody(r.data.body);
      toast.success("Courriel généré par l'IA ✨");
    } catch (e) { toast.error("Erreur IA : " + (e?.response?.data?.detail || e.message)); }
    setBusy(false);
  }

  async function envoyer() {
    if (!to.trim()) return toast.error("Destinataire requis");
    if (!subject.trim() || !body.trim()) return toast.error("Objet et corps requis");
    setBusy(true);
    try {
      await api.post(`/immeubles/${immeubleId}/courriels/envoyer`, {
        to: to.trim(), subject, body, porte_id: porteId || null,
        type: modeles.find((m) => m.key === tplKey)?.label || "communication",
      });
      toast.success(`Courriel envoyé à ${to.trim()}`);
      setSubject(""); setBody(""); setTplKey("");
      loadComms();
    } catch (e) {
      const d = e?.response?.data?.detail || e.message;
      toast.error(d.includes("Gmail") ? "Connecte ton Gmail (menu Intégrations) — " + d : "Échec de l'envoi : " + d);
    }
    setBusy(false);
  }

  async function setStatus(c, field, value) {
    try { await api.patch(`/immeubles/communications/${c.id}`, { [field]: value }); loadComms(); }
    catch { toast.error("Erreur de mise à jour"); }
  }

  return (
    <div className="space-y-6" data-testid="courriels-panel">
      <div className="border border-zinc-800 rounded-xl p-4 space-y-3 bg-zinc-900/30">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Porte</label>
            <select value={porteId} onChange={(e) => setPorteId(e.target.value)} data-testid="courriel-porte-select"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100">
              {portes.map((p) => <option key={p.id} value={p.id}>Porte {p.identifiant} — {p.locataire_nom || "?"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Modèle</label>
            <select value={tplKey} onChange={(e) => applyTemplate(e.target.value)} data-testid="courriel-template-select"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100">
              <option value="">— Choisir un modèle —</option>
              {modeles.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Nouveau loyer (hausse, $)</label>
            <input type="number" value={nouveauLoyer} onChange={(e) => setNouveauLoyer(e.target.value)} data-testid="courriel-nouveau-loyer"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Destinataire</label>
            <input value={to} onChange={(e) => setTo(e.target.value)} data-testid="courriel-to" placeholder="courriel@locataire.com"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" />
          </div>
        </div>

        <div>
          <label className="text-xs text-zinc-400 mb-1 block">Objet</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="courriel-subject"
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" />
        </div>
        <div>
          <label className="text-xs text-zinc-400 mb-1 block">Corps du courriel</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="courriel-body" rows={12}
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200" />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={genererIA} disabled={busy} data-testid="generer-ia-btn"
            className="bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
            <Sparkle size={16} weight="fill" /> {busy ? "…" : "Personnaliser avec l'IA (Claude)"}
          </button>
          <button onClick={envoyer} disabled={busy} data-testid="envoyer-courriel-btn"
            className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
            <PaperPlaneTilt size={16} weight="fill" /> Envoyer via Gmail
          </button>
          <span className="text-xs text-zinc-500">Le courriel ne remplace pas l'avis officiel du TAL.</span>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-zinc-300 mb-3">Ligne du temps des communications</h3>
        {comms.length === 0 ? (
          <div className="text-zinc-500 text-sm">Aucune communication envoyée pour le moment.</div>
        ) : (
          <div className="space-y-2" data-testid="communications-timeline">
            {comms.map((c) => (
              <div key={c.id} className="border border-zinc-800 rounded-xl p-3 bg-zinc-900/30" data-testid={`comm-${c.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm font-semibold text-zinc-100">{c.subject}</div>
                  <div className="text-xs text-zinc-500">{(c.sent_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <div className="text-xs text-zinc-400 mt-0.5">À : {c.to} · {c.type}</div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded ${PROOF_COLORS[c.proof_status] || "bg-zinc-700 text-zinc-300"}`}>Preuve : {c.proof_status}</span>
                  <select value={c.proof_status} onChange={(e) => setStatus(c, "proof_status", e.target.value)} data-testid={`proof-select-${c.id}`}
                    className="bg-zinc-900 border border-zinc-700 rounded px-2 py-0.5 text-xs text-zinc-200">
                    {["envoyé", "preuve téléversée", "accusé reçu"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <span className={`text-xs px-2 py-0.5 rounded ${RESP_COLORS[c.response_status] || "bg-zinc-700 text-zinc-300"}`}>Réponse : {c.response_status}</span>
                  <select value={c.response_status} onChange={(e) => setStatus(c, "response_status", e.target.value)} data-testid={`resp-select-${c.id}`}
                    className="bg-zinc-900 border border-zinc-700 rounded px-2 py-0.5 text-xs text-zinc-200">
                    {["en attente", "reçue", "acceptation", "refus"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// COFFRE DE DOCUMENTS
// ════════════════════════════════════════════════════════════════════════════
function DocumentsPanel({ immeubleId, portes }) {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [file, setFile] = useState(null);
  const [categorie, setCategorie] = useState("Bail signé");
  const [note, setNote] = useState("");
  const [porteId, setPorteId] = useState("");
  const [uploading, setUploading] = useState(false);

  async function load() {
    try { const r = await api.get(`/immeubles/${immeubleId}/documents`); setDocs(r.data.documents || []); setCategories(r.data.categories || []); }
    catch { toast.error("Erreur de chargement"); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [immeubleId]);

  async function upload() {
    if (!file) return toast.error("Choisis un fichier");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("categorie", categorie); fd.append("note", note); fd.append("porte_id", porteId);
      await api.post(`/immeubles/${immeubleId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document téléversé");
      setFile(null); setNote("");
      document.querySelector("[data-testid='doc-file-input']").value = "";
      load();
    } catch (e) { toast.error("Erreur : " + (e?.response?.data?.detail || e.message)); }
    setUploading(false);
  }

  async function view(d) {
    try {
      const r = await api.get(`/immeubles/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch { toast.error("Erreur d'ouverture"); }
  }
  async function del(id) {
    if (!window.confirm("Supprimer ce document ?")) return;
    try { await api.delete(`/immeubles/documents/${id}`); toast.success("Supprimé"); load(); }
    catch { toast.error("Erreur de suppression"); }
  }

  const porteLabel = (pid) => { const p = portes.find((x) => x.id === pid); return p ? `Porte ${p.identifiant}` : ""; };

  return (
    <div className="space-y-5" data-testid="documents-panel">
      <div className="border border-zinc-800 rounded-xl p-4 space-y-3 bg-zinc-900/30">
        <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-widest">Téléverser un document</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Fichier (PDF, image…)</label>
            <input type="file" onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file-input"
              className="w-full text-sm text-zinc-300 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-amber-500 file:text-zinc-950 file:font-semibold" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Catégorie</label>
            <select value={categorie} onChange={(e) => setCategorie(e.target.value)} data-testid="doc-categorie-select"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Porte (optionnel)</label>
            <select value={porteId} onChange={(e) => setPorteId(e.target.value)} data-testid="doc-porte-select"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100">
              <option value="">— Aucune —</option>
              {portes.map((p) => <option key={p.id} value={p.id}>Porte {p.identifiant}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Note</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="doc-note-input"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" />
          </div>
        </div>
        <button onClick={upload} disabled={uploading} data-testid="upload-doc-btn"
          className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
          <UploadSimple size={16} weight="bold" /> {uploading ? "Téléversement…" : "Téléverser"}
        </button>
      </div>

      <div className="space-y-2" data-testid="documents-list">
        {docs.length === 0 ? (
          <div className="text-center py-8 text-zinc-500 text-sm">Aucun document.</div>
        ) : docs.map((d) => (
          <div key={d.id} className="border border-zinc-800 rounded-xl p-3 flex items-center justify-between gap-3" data-testid={`document-row-${d.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <FilePdf size={26} weight="fill" className="text-amber-500 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-100 truncate">{d.original_filename}</div>
                <div className="text-xs text-zinc-500">{d.categorie}{d.porte_id ? ` · ${porteLabel(d.porte_id)}` : ""}{d.note ? ` · ${d.note}` : ""}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button onClick={() => view(d)} className="text-amber-400 hover:text-amber-300" data-testid={`view-doc-${d.id}`} title="Ouvrir"><Eye size={18} weight="fill" /></button>
              <button onClick={() => del(d.id)} className="text-zinc-500 hover:text-red-400" data-testid={`del-doc-${d.id}`}><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



// ════════════════════════════════════════════════════════════════════════════
// DÉPENSES & TAL — suivi annuel par catégorie + estimation d'ajustement de loyer
// ════════════════════════════════════════════════════════════════════════════
const DEP_CATS = [
  ["taxes_municipales", "Taxes municipales"], ["taxes_scolaires", "Taxes scolaires"],
  ["assurances", "Assurances (incendie/responsabilité)"], ["electricite", "Électricité (proprio)"],
  ["chauffage", "Chauffage (proprio)"], ["telecom", "Internet / télécom"],
  ["entretien", "Entretien & réparations"], ["deneigement", "Déneigement / terrain"],
  ["gestion", "Gestion / honoraires"], ["travaux_immobilisation", "Travaux majeurs (immobilisations)"],
  ["autres", "Autres dépenses"],
];

function DepensesPanel({ immeubleId, portes }) {
  const [data, setData] = useState(null);
  const [tal, setTal] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [annee, setAnnee] = useState(null);
  const [form, setForm] = useState({ categorie: "electricite", montant: "", date_depense: "", fournisseur: "", porte_id: "", proprio_assume_pct: 100, note: "" });
  const [busy, setBusy] = useState(false);
  const [avisTextes, setAvisTextes] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function load(yr) {
    try {
      const q = yr ? `?annee=${yr}` : "";
      const r = await api.get(`/immeubles/${immeubleId}/depenses${q}`);
      setData(r.data); setAnnee(r.data.annee);
      const t = await api.get(`/immeubles/${immeubleId}/tal-estimation${q}`);
      setTal(t.data);
    } catch { toast.error("Erreur de chargement des dépenses"); }
  }
  async function loadSuggestions() {
    try { const r = await api.get(`/immeubles/${immeubleId}/depenses/suggestions`); setSuggestions(r.data.suggestions || []); }
    catch { /* ignore */ }
  }
  useEffect(() => { load(null); loadSuggestions(); /* eslint-disable-next-line */ }, [immeubleId]);

  async function addDepense() {
    if (!form.montant) return toast.error("Montant requis");
    setBusy(true);
    try {
      await api.post(`/immeubles/${immeubleId}/depenses`, {
        ...form, montant: Number(form.montant),
        proprio_assume_pct: Number(form.proprio_assume_pct || 100),
        porte_id: form.porte_id || null,
      });
      toast.success("Dépense ajoutée");
      setForm({ categorie: "electricite", montant: "", date_depense: "", fournisseur: "", porte_id: "", proprio_assume_pct: 100, note: "" });
      load(annee);
    } catch (e) { toast.error("Erreur : " + (e?.response?.data?.detail || e.message)); }
    setBusy(false);
  }
  async function delDepense(id) {
    if (!window.confirm("Supprimer cette dépense ?")) return;
    try { await api.delete(`/immeubles/depenses/${id}`); toast.success("Supprimée"); load(annee); loadSuggestions(); }
    catch { toast.error("Erreur de suppression"); }
  }

  function pickAndUploadPiece(depId) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,application/pdf";
    input.onchange = async () => {
      const f = input.files[0];
      if (!f) return;
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/immeubles/${immeubleId}/depenses/${depId}/piece`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Pièce justificative attachée");
        load(annee);
      } catch (e) { toast.error("Erreur : " + (e?.response?.data?.detail || e.message)); }
    };
    input.click();
  }
  async function viewPiece(docId) {
    try {
      const r = await api.get(`/immeubles/documents/${docId}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Erreur d'ouverture de la pièce"); }
  }
  async function detachPiece(depId) {
    if (!window.confirm("Détacher la pièce justificative ?")) return;
    try { await api.delete(`/immeubles/depenses/${depId}/piece`); toast.success("Pièce détachée"); load(annee); }
    catch { toast.error("Erreur"); }
  }
  async function linkSuggestion(s) {
    try {
      await api.post(`/immeubles/${immeubleId}/depenses/link`, {
        receipt_ids: [s.receipt_id], categorie: s.categorie_suggeree, proprio_assume_pct: 100,
      });
      toast.success(`Reçu ${s.merchant} rattaché`);
      load(annee); loadSuggestions();
    } catch (e) { toast.error("Erreur : " + (e?.response?.data?.detail || e.message)); }
  }

  async function genererDossierPdf() {
    try {
      const r = await api.get(`/immeubles/${immeubleId}/tal-dossier/pdf?annee=${annee || ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      toast.success("Dossier PDF généré");
    } catch { toast.error("Erreur de génération du dossier PDF"); }
  }

  async function preparerAvis() {
    const to = window.prompt("Courriel de la directrice (Salomée Mc Kenzie — Mino Obigiwasin) pour les avis de renouvellement :", "");
    if (to === null) return;
    if (!to.trim()) return toast.error("Courriel requis");
    const date_effet = window.prompt("Date d'entrée en vigueur du renouvellement (AAAA-MM-JJ) :", "2026-07-01");
    if (date_effet === null) return;
    setBusy(true);
    try {
      const r = await api.post(`/immeubles/${immeubleId}/avis-renouvellement/drafts`, {
        to: to.trim(), date_effet: date_effet.trim(), annee: annee, combiner: true,
      });
      if (r.data.gmail_ok) {
        toast.success(`${(r.data.brouillons || []).length} brouillon(s) créé(s) dans Gmail — relis puis Envoie.`);
        setAvisTextes(null);
      } else {
        // Gmail déconnecté : on affiche les textes à copier
        setAvisTextes({ avis: r.data.avis || [], destinataire: r.data.destinataire || to.trim(), note: r.data.note });
        toast.message("Gmail déconnecté — textes des avis affichés (à copier).");
      }
    } catch (e) {
      const d = e?.response?.data?.detail || e.message;
      toast.error("Erreur : " + d);
    }
    setBusy(false);
  }

  if (!data || !tal) return <div className="text-zinc-500 py-8">Chargement…</div>;

  const catLabel = (k) => (DEP_CATS.find((c) => c[0] === k) || [k, k])[1];

  return (
    <div className="space-y-6" data-testid="depenses-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-xs text-zinc-500 max-w-2xl">
          Suivi annuel des dépenses du duplex (à <b>ton nom personnel</b>) pour justifier une hausse de loyer au TAL.
          Taxes/assurances/énergie sont calculées sur la <b>variation réelle</b> année/année.
        </p>
        <div className="flex items-center gap-2">
          <label className="text-sm text-zinc-400">Année</label>
          <select value={annee || ""} onChange={(e) => { setAnnee(Number(e.target.value)); load(Number(e.target.value)); }}
            data-testid="depense-annee-select" className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100">
            {(data.annees_disponibles.length ? data.annees_disponibles : [data.annee]).map((y) => <option key={y} value={y}>{y}</option>)}
            {!data.annees_disponibles.includes(data.annee) && <option value={data.annee}>{data.annee}</option>}
          </select>
        </div>
      </div>

      {/* Actions : dossier PDF + avis de renouvellement */}
      <div className="flex flex-wrap gap-2">
        <button onClick={genererDossierPdf} data-testid="dossier-pdf-btn"
          className="bg-zinc-800 hover:bg-zinc-700 text-amber-400 border border-amber-500/30 font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
          <FilePdf size={16} weight="fill" /> Générer le dossier de justification (PDF)
        </button>
        <button onClick={preparerAvis} disabled={busy} data-testid="avis-renouvellement-btn"
          className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
          <PaperPlaneTilt size={16} weight="fill" /> Préparer les avis de renouvellement (brouillons Gmail)
        </button>
      </div>

      {/* Textes des avis (affichés si Gmail déconnecté — à copier) */}
      {avisTextes && (
        <div className="rounded-xl border border-amber-500/40 bg-zinc-900 p-5 space-y-4" data-testid="avis-textes-panel">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-amber-400 font-semibold flex items-center gap-2"><PaperPlaneTilt size={16} weight="fill" /> Avis de renouvellement — à copier</div>
              <p className="text-xs text-zinc-400 mt-1">{avisTextes.note}</p>
              <p className="text-xs text-zinc-500 mt-1">Destinataire : <b className="text-zinc-300">{avisTextes.destinataire}</b></p>
            </div>
            <button onClick={() => setAvisTextes(null)} className="text-zinc-500 hover:text-zinc-200 text-sm" data-testid="avis-textes-close">Fermer ✕</button>
          </div>
          {avisTextes.avis.map((a, i) => (
            <div key={i} className="rounded-lg border border-zinc-800 bg-zinc-950 p-3" data-testid={`avis-texte-${i}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-sm font-semibold text-zinc-200 truncate">{a.subject}</div>
                <button
                  onClick={() => { navigator.clipboard.writeText(`Objet : ${a.subject}\n\n${a.body}`); toast.success("Avis copié dans le presse-papiers"); }}
                  className="shrink-0 bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs font-semibold px-3 py-1.5 rounded-lg"
                  data-testid={`avis-copy-${i}`}>
                  Copier l'avis
                </button>
              </div>
              <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-sans max-h-72 overflow-y-auto">{a.body}</pre>
            </div>
          ))}
        </div>
      )}


      {/* Estimation TAL */}
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5 space-y-3" data-testid="tal-estimation">
        <div className="flex items-center gap-2 text-amber-400">
          <Calculator size={18} weight="fill" />
          <h3 className="font-bold">Estimation d'ajustement de loyer — TAL {tal.annee}</h3>
        </div>
        <div className="text-xs text-zinc-400 grid sm:grid-cols-3 gap-2">
          <span>Loyer de base : <b className="text-zinc-200">{tal.rates.base_rate} %</b></span>
          <span>Immobilisations : <b className="text-zinc-200">{tal.rates.capex_rate} %</b></span>
          <span>Variation taxes : <b className="text-zinc-200">{money(tal.variation_taxes)}</b> · assurances : <b className="text-zinc-200">{money(tal.variation_assurances)}</b> · énergie : <b className="text-zinc-200">{money(tal.variation_energie)}</b></span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-zinc-500 border-b border-zinc-800">
              <th className="py-2 px-2">Porte</th><th className="py-2 px-2 text-right">Loyer actuel</th>
              <th className="py-2 px-2 text-right">Base</th><th className="py-2 px-2 text-right">Taxes</th>
              <th className="py-2 px-2 text-right">Assur.</th><th className="py-2 px-2 text-right">Énergie</th>
              <th className="py-2 px-2 text-right">Travaux</th><th className="py-2 px-2 text-right">Hausse/mois</th>
              <th className="py-2 px-2 text-right">Nouveau loyer</th>
            </tr></thead>
            <tbody>
              {tal.portes.map((p) => (
                <tr key={p.porte_id} className="border-b border-zinc-900" data-testid={`tal-porte-${p.identifiant}`}>
                  <td className="py-2 px-2 text-zinc-200">{p.identifiant}</td>
                  <td className="py-2 px-2 text-right text-zinc-300">{money(p.loyer_actuel)}</td>
                  <td className="py-2 px-2 text-right text-zinc-400">+{money(p.composantes.base)}</td>
                  <td className="py-2 px-2 text-right text-zinc-400">+{money(p.composantes.taxes)}</td>
                  <td className="py-2 px-2 text-right text-zinc-400">+{money(p.composantes.assurances)}</td>
                  <td className="py-2 px-2 text-right text-zinc-400">+{money(p.composantes.energie)}</td>
                  <td className="py-2 px-2 text-right text-zinc-400">+{money(p.composantes.travaux)}</td>
                  <td className="py-2 px-2 text-right text-amber-400 font-semibold">+{money(p.hausse_mensuelle)} {p.pourcentage_hausse != null && <span className="text-xs text-zinc-500">({p.pourcentage_hausse}%)</span>}</td>
                  <td className="py-2 px-2 text-right text-green-400 font-bold">{money(p.nouveau_loyer)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-amber-200/80 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">⚠️ {tal.avertissement}</div>
      </div>

      {/* Suggestions de rattachement */}
      {suggestions.length > 0 && (
        <div className="space-y-2" data-testid="depense-suggestions">
          <h3 className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
            <LinkSimple size={16} weight="bold" className="text-amber-500" /> Reçus WellyBox à rattacher ({suggestions.length})
          </h3>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {suggestions.map((s) => (
              <div key={s.receipt_id} className="flex items-center justify-between gap-3 border border-zinc-800 rounded-lg px-3 py-2 bg-zinc-900/30" data-testid={`suggestion-${s.receipt_id}`}>
                <div className="text-sm min-w-0">
                  <span className="text-zinc-200 font-medium">{s.merchant}</span>
                  <span className="text-zinc-500"> · {money(s.montant)} · {s.date || "?"}</span>
                  <span className="ml-2 text-xs px-2 py-0.5 rounded bg-amber-500/15 text-amber-300">{s.categorie_suggeree_label}</span>
                </div>
                <button onClick={() => linkSuggestion(s)} data-testid={`link-suggestion-${s.receipt_id}`}
                  className="shrink-0 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-3 py-1 rounded-lg text-xs flex items-center gap-1">
                  <LinkSimple size={13} weight="bold" /> Rattacher
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ajout manuel */}
      <div className="border border-zinc-800 rounded-xl p-4 space-y-3 bg-zinc-900/30">
        <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-widest flex items-center gap-1.5"><Receipt size={15} weight="fill" /> Ajouter une dépense</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Catégorie</label>
            <select value={form.categorie} onChange={(e) => set("categorie", e.target.value)} data-testid="depense-categorie-select"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100">
              {DEP_CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Montant ($)</label>
            <input type="number" value={form.montant} onChange={(e) => set("montant", e.target.value)} data-testid="depense-montant"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Date (AAAA-MM-JJ)</label>
            <input type="date" value={form.date_depense} onChange={(e) => set("date_depense", e.target.value)} data-testid="depense-date"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Fournisseur</label>
            <input value={form.fournisseur} onChange={(e) => set("fournisseur", e.target.value)} data-testid="depense-fournisseur"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Porte (optionnel)</label>
            <select value={form.porte_id} onChange={(e) => set("porte_id", e.target.value)} data-testid="depense-porte-select"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100">
              <option value="">— Immeuble entier —</option>
              {portes.map((p) => <option key={p.id} value={p.id}>Porte {p.identifiant}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">% assumé par le proprio</label>
            <input type="number" value={form.proprio_assume_pct} onChange={(e) => set("proprio_assume_pct", e.target.value)} data-testid="depense-pct"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" />
          </div>
          <div className="lg:col-span-2">
            <label className="text-xs text-zinc-400 mb-1 block">Note</label>
            <input value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="depense-note"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" />
          </div>
        </div>
        <button onClick={addDepense} disabled={busy} data-testid="add-depense-btn"
          className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
          <Plus size={16} weight="bold" /> {busy ? "…" : "Ajouter la dépense"}
        </button>
      </div>

      {/* Tableau récap par catégorie */}
      <div>
        <h3 className="text-sm font-semibold text-zinc-300 mb-2">Récapitulatif {data.annee} (vs {data.annee_precedente})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-zinc-500 border-b border-zinc-800">
              <th className="py-2 px-3">Catégorie</th><th className="py-2 px-3 text-right">{data.annee}</th>
              <th className="py-2 px-3 text-right">{data.annee_precedente}</th><th className="py-2 px-3 text-right">Variation</th>
            </tr></thead>
            <tbody>
              {data.categories.filter((c) => c.montant_annee || c.montant_precedent).map((c) => (
                <tr key={c.key} className="border-b border-zinc-900" data-testid={`depense-cat-${c.key}`}>
                  <td className="py-2 px-3 text-zinc-200">{c.label}</td>
                  <td className="py-2 px-3 text-right text-zinc-300">{money(c.montant_annee)}</td>
                  <td className="py-2 px-3 text-right text-zinc-500">{money(c.montant_precedent)}</td>
                  <td className={`py-2 px-3 text-right ${c.variation > 0 ? "text-amber-400" : c.variation < 0 ? "text-green-400" : "text-zinc-500"}`}>{c.variation > 0 ? "+" : ""}{money(c.variation)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-zinc-700 font-bold">
                <td className="py-2 px-3 text-zinc-100">TOTAL</td>
                <td className="py-2 px-3 text-right text-zinc-100">{money(data.total_annee)}</td>
                <td className="py-2 px-3 text-right text-zinc-400">{money(data.total_precedent)}</td>
                <td className={`py-2 px-3 text-right ${data.total_annee - data.total_precedent > 0 ? "text-amber-400" : "text-green-400"}`}>{data.total_annee - data.total_precedent > 0 ? "+" : ""}{money(data.total_annee - data.total_precedent)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Liste détaillée */}
      <div>
        <h3 className="text-sm font-semibold text-zinc-300 mb-2">Dépenses détaillées</h3>
        {data.depenses.length === 0 ? (
          <div className="text-center py-6 text-zinc-500 text-sm">Aucune dépense enregistrée. Rattache des reçus WellyBox ou ajoute-les manuellement.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="depenses-list">
              <thead><tr className="text-left text-zinc-500 border-b border-zinc-800">
                <th className="py-2 px-3">Date</th><th className="py-2 px-3">Catégorie</th><th className="py-2 px-3">Fournisseur</th>
                <th className="py-2 px-3 text-right">Montant</th><th className="py-2 px-3">Source</th><th className="py-2 px-3">Pièce</th><th className="py-2 px-3"></th>
              </tr></thead>
              <tbody>
                {data.depenses.map((d) => (
                  <tr key={d.id} className="border-b border-zinc-900" data-testid={`depense-row-${d.id}`}>
                    <td className="py-2 px-3 text-zinc-400">{(d.date_depense || "").slice(0, 10) || "—"}</td>
                    <td className="py-2 px-3 text-zinc-200">{catLabel(d.categorie)}</td>
                    <td className="py-2 px-3 text-zinc-400">{d.fournisseur || "—"}</td>
                    <td className="py-2 px-3 text-right text-zinc-300">{money(d.montant)}{d.proprio_assume_pct != null && d.proprio_assume_pct !== 100 ? <span className="text-xs text-zinc-500"> ({d.proprio_assume_pct}%)</span> : null}</td>
                    <td className="py-2 px-3"><span className={`text-xs px-2 py-0.5 rounded ${d.source === "receipt" ? "bg-blue-500/15 text-blue-300" : "bg-zinc-700 text-zinc-300"}`}>{d.source === "receipt" ? "WellyBox" : "Manuel"}</span></td>
                    <td className="py-2 px-3">
                      {d.document_id ? (
                        <div className="flex items-center gap-2">
                          <button onClick={() => viewPiece(d.document_id)} className="text-green-400 hover:text-green-300 flex items-center gap-1 text-xs" data-testid={`view-piece-${d.id}`} title={d.piece_filename || "Voir la pièce"}>
                            <Paperclip size={14} weight="fill" /> Voir
                          </button>
                          <button onClick={() => detachPiece(d.id)} className="text-zinc-600 hover:text-red-400" data-testid={`detach-piece-${d.id}`} title="Détacher"><X size={13} /></button>
                        </div>
                      ) : (
                        <button onClick={() => pickAndUploadPiece(d.id)} className="text-zinc-500 hover:text-amber-400 flex items-center gap-1 text-xs" data-testid={`add-piece-${d.id}`}>
                          <Paperclip size={14} /> Joindre
                        </button>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right"><button onClick={() => delDepense(d.id)} className="text-zinc-500 hover:text-red-400" data-testid={`del-depense-${d.id}`}><Trash size={15} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


// ════════════════════════════ DOSSIER DE REFINANCEMENT ════════════════════════════
const REFI_DEBT_STATUSES = {
  a_documenter: "À documenter", releve_recu: "Relevé reçu", montant_confirme: "Montant final confirmé",
  autorise_notaire: "Autorisé au notaire", payee: "Payée", solde_zero: "Solde zéro confirmé", litige: "Litige / à vérifier",
};
const REFI_DOC_STATUSES = { manquant: "Manquant", en_cours: "En cours", recu: "Reçu", a_mettre_a_jour: "À mettre à jour" };

function RefiDossierPanel({ immeubleId }) {
  const [courtier, setCourtier] = useState(null);
  const [debts, setDebts] = useState([]);
  const [debtTotals, setDebtTotals] = useState({});
  const [docs, setDocs] = useState([]);
  const [fees, setFees] = useState({ penalite: 3000, frais_notaire: 1500, evaluation: 500, frais_cloture: 0 });
  const [faisa, setFaisa] = useState(null);
  const [prop, setProp] = useState({ valeur_marche: "", solde_hypothecaire: "" });
  const [activity, setActivity] = useState([]);

  const loadAll = async () => {
    try {
      const [c, d, doc] = await Promise.all([
        api.get(`/immeubles/${immeubleId}/refi/courtier`),
        api.get(`/immeubles/${immeubleId}/refi/debts`),
        api.get(`/immeubles/${immeubleId}/refi/documents`),
      ]);
      setCourtier(c.data.courtier);
      setDebts(d.data.debts || []); setDebtTotals(d.data.totals || {});
      setDocs(doc.data.documents || []);
    } catch { toast.error("Erreur de chargement du dossier"); }
    try { const a = await api.get(`/immeubles/${immeubleId}/refi/activity`); setActivity(a.data.items || []); } catch { /* noop */ }
  };
  const loadFaisa = async () => {
    try {
      const q = new URLSearchParams({ target_ltv: 80, ...fees }).toString();
      const r = await api.get(`/immeubles/${immeubleId}/refi/faisabilite?${q}`);
      setFaisa(r.data);
      setProp((pv) => ({
        valeur_marche: pv.valeur_marche === "" && r.data.valeur_marche ? r.data.valeur_marche : pv.valeur_marche,
        solde_hypothecaire: pv.solde_hypothecaire === "" && r.data.solde_hypothecaire ? r.data.solde_hypothecaire : pv.solde_hypothecaire,
      }));
    } catch { /* noop */ }
  };
  useEffect(() => { loadAll(); }, [immeubleId]);
  useEffect(() => { if (immeubleId) loadFaisa(); }, [immeubleId, debts, fees]);

  const seedDebts = async () => { await api.post(`/immeubles/${immeubleId}/refi/debts/seed`); toast.success("Dettes initiales ajoutées"); loadAll(); };
  const updateDebt = async (id, patch) => {
    setDebts((p) => p.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    try { await api.put(`/immeubles/refi/debts/${id}`, patch); loadAll(); } catch { toast.error("Erreur"); }
  };
  const delDebt = async (id) => { if (!window.confirm("Supprimer cette dette ?")) return; await api.delete(`/immeubles/refi/debts/${id}`); loadAll(); };
  const addDebt = async () => { await api.post(`/immeubles/${immeubleId}/refi/debts`, { creancier: "Nouveau créancier", type: "Autre", solde_declare: 0 }); loadAll(); };
  const updateDoc = async (id, patch) => {
    setDocs((p) => p.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    try { await api.put(`/immeubles/refi/documents/${id}`, patch); } catch { toast.error("Erreur"); }
  };
  const uploadDoc = async (id, file) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try { await api.post(`/immeubles/refi/documents/${id}/upload`, { filename: file.name, content_base64: reader.result }); toast.success("Fichier téléversé"); loadAll(); }
      catch { toast.error("Échec du téléversement"); }
    };
    reader.readAsDataURL(file);
  };
  const copyLink = async () => {
    const url = courtier?.portail_url || "";
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success("Lien copié");
      } catch { toast.error("Copie impossible — copiez manuellement le lien"); }
    }
  };

  const saveProp = async () => {
    try {
      await api.put(`/immeubles/${immeubleId}`, {
        valeur_marche: prop.valeur_marche === "" ? null : Number(prop.valeur_marche),
        solde_hypothecaire: prop.solde_hypothecaire === "" ? null : Number(prop.solde_hypothecaire),
      });
      toast.success("Données enregistrées");
      loadFaisa();
    } catch { toast.error("Erreur d'enregistrement"); }
  };

  const downloadReport = async () => {
    try {
      const q = new URLSearchParams({ target_ltv: 80, ...fees }).toString();
      const r = await api.get(`/immeubles/${immeubleId}/refi/report-pdf?${q}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Échec de la génération du PDF"); }
  };

  const readiness = (() => {
    const docsPct = docs.length ? Math.round((docs.filter((d) => d.statut === "recu").length / docs.length) * 100) : 0;
    const debtsPct = debts.length ? Math.round((debts.filter((d) => d.statut === "solde_zero").length / debts.length) * 100) : 0;
    const ecartOk = faisa?.ecart != null && faisa.faisable;
    const global = Math.round((docsPct + debtsPct + (ecartOk ? 100 : 0)) / 3);
    return { docsPct, debtsPct, ecartOk, global };
  })();

  if (!courtier) return <div className="text-zinc-500 py-8">Chargement du dossier…</div>;

  return (
    <div className="space-y-6" data-testid="refi-dossier-panel">
      {/* Bandeau sécurité */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2 text-xs text-amber-200">
        <ShieldCheck size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
        <span>Dossier interne sécurisé. Le portail du courtier reste externe — les renseignements bancaires, NAS et mots de passe s'y remplissent directement, jamais dans Kaizen. Chaque dépôt/téléchargement de document est journalisé.</span>
      </div>

      {/* Barre de progression globale du dossier */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="refi-progress">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-zinc-100">Préparation du dossier</span>
          <span className={`text-sm font-bold ${readiness.global >= 100 ? "text-emerald-400" : readiness.global >= 50 ? "text-amber-400" : "text-zinc-300"}`} data-testid="refi-readiness-global">{readiness.global}%</span>
        </div>
        <div className="w-full h-2.5 bg-zinc-800 rounded-full overflow-hidden mb-3">
          <div className={`h-full rounded-full transition-all ${readiness.global >= 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${readiness.global}%` }} />
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div><span className="text-zinc-500">Documents reçus</span><div className="text-zinc-200 font-semibold">{readiness.docsPct}%</div></div>
          <div><span className="text-zinc-500">Dettes soldées (zéro)</span><div className="text-zinc-200 font-semibold">{readiness.debtsPct}%</div></div>
          <div><span className="text-zinc-500">Écart positif</span><div className={`font-semibold ${readiness.ecartOk ? "text-emerald-400" : "text-zinc-400"}`}>{readiness.ecartOk ? "✓ Oui" : "— En attente"}</div></div>
        </div>
        {readiness.global >= 100 && (
          <div className="mt-3 flex items-center justify-between gap-2 bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-3" data-testid="refi-ready-cta">
            <span className="text-sm text-emerald-300 font-semibold">🎉 Dossier prêt à soumettre au courtier !</span>
            <button onClick={downloadReport} data-testid="refi-ready-download"
              className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5"><FilePdf size={14} weight="fill" /> Télécharger le rapport</button>
          </div>
        )}
      </div>

      {/* FICHE COURTIER */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="refi-courtier-card">
        <div className="flex items-center gap-2 mb-3"><Handshake size={18} className="text-emerald-400" /><span className="font-semibold text-zinc-100">{courtier.dossier}</span>
          <span className="text-[10px] uppercase px-2 py-0.5 rounded-full border border-sky-500/40 text-sky-300">{courtier.statut}</span></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-zinc-300">
          <div><span className="text-zinc-500">Courtier :</span> {courtier.courtier}</div>
          <div><span className="text-zinc-500">Contact du dossier :</span> {courtier.contact}</div>
          <div><span className="text-zinc-500">Téléphone :</span> {courtier.telephone}</div>
          <div><span className="text-zinc-500">Courriel :</span> {courtier.courriel}</div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <a href={courtier.portail_url} target="_blank" rel="noopener noreferrer" data-testid="refi-open-portal"
            className="flex items-center gap-1.5 text-sm bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-3 py-1.5 rounded-lg"><ArrowSquareOut size={15} weight="bold" /> Ouvrir le portail sécurisé</a>
          <button onClick={copyLink} data-testid="refi-copy-link" className="flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-3 py-1.5 rounded-lg"><Copy size={15} /> Copier le lien</button>
          <a href={`mailto:${courtier.courriel}?subject=Dossier%20de%20refinancement%20-%20Duplex%20Br%C3%A9beuf`} data-testid="refi-email-broker"
            className="flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-3 py-1.5 rounded-lg"><EnvelopeSimple size={15} /> Envoyer un courriel au courtier</a>
          <button onClick={downloadReport} data-testid="refi-report-pdf"
            className="flex items-center gap-1.5 text-sm bg-amber-600 hover:bg-amber-500 text-black font-semibold px-3 py-1.5 rounded-lg"><FilePdf size={15} weight="fill" /> Rapport PDF</button>
        </div>
      </div>

      {/* FAISABILITÉ */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="refi-faisabilite">
        <div className="flex items-center gap-2 mb-3"><Calculator size={18} className="text-amber-400" /><span className="font-semibold text-zinc-100">Calcul de faisabilité</span>
          <span className="text-[10px] italic text-amber-300">{faisa?.disclaimer}</span></div>
        {/* Saisie valeur marchande + solde hypothécaire → débloque cash-out & écart */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3 bg-zinc-950 border border-zinc-800 rounded-lg p-3">
          <label className="text-xs text-zinc-400">Valeur marchande ($)
            <input type="number" value={prop.valeur_marche} onChange={(e) => setProp((p) => ({ ...p, valeur_marche: e.target.value }))}
              data-testid="refi-valeur-marche" placeholder="ex. 425000" className="w-full mt-0.5 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-100" /></label>
          <label className="text-xs text-zinc-400">Solde hypothécaire ($)
            <input type="number" value={prop.solde_hypothecaire} onChange={(e) => setProp((p) => ({ ...p, solde_hypothecaire: e.target.value }))}
              data-testid="refi-solde-hypo" placeholder="ex. 238000" className="w-full mt-0.5 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-100" /></label>
          <div className="flex items-end"><button onClick={saveProp} data-testid="refi-save-prop" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded px-3 py-1.5">Enregistrer & recalculer</button></div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          {[["penalite", "Pénalité hypothécaire"], ["frais_notaire", "Frais notariés"], ["evaluation", "Évaluation"], ["frais_cloture", "Frais de clôture"]].map(([k, l]) => (
            <label key={k} className="text-xs text-zinc-400">{l}
              <input type="number" value={fees[k]} onChange={(e) => setFees((f) => ({ ...f, [k]: e.target.value }))}
                data-testid={`refi-fee-${k}`} className="w-full mt-0.5 bg-zinc-950 border border-zinc-700 rounded px-2 py-1 text-zinc-100" />
            </label>
          ))}
        </div>
        {faisa?.donnees_manquantes?.length > 0 && (
          <div className="text-xs text-amber-300 mb-2">⚠ Données manquantes : {faisa.donnees_manquantes.join(", ")} — saisis-les dans l'onglet « Immeuble ».</div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[["LTV actuel", faisa?.ltv_actuel != null ? faisa.ltv_actuel + " %" : "—"],
            ["Financement max (80%)", faisa?.max_financing != null ? money(faisa.max_financing) : "—"],
            ["Cash-out brut", faisa?.cash_out_brut != null ? money(faisa.cash_out_brut) : "—"],
            ["Dettes à consolider", money(faisa?.dettes_a_consolider)],
            ["Besoin refinancement", money(faisa?.besoin_refinancement)]].map(([l, v]) => (
            <div key={l} className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
              <div className="text-[11px] text-zinc-500">{l}</div><div className="text-zinc-100 font-semibold">{v}</div>
            </div>
          ))}
          <div className={`rounded-lg p-3 border ${faisa?.ecart == null ? "bg-zinc-950 border-zinc-800" : faisa.faisable ? "bg-emerald-500/10 border-emerald-500/40" : "bg-rose-500/10 border-rose-500/40"}`} data-testid="refi-ecart">
            <div className="text-[11px] text-zinc-400">Écart (cash-out − besoin)</div>
            <div className={`font-bold ${faisa?.ecart == null ? "text-zinc-100" : faisa.faisable ? "text-emerald-300" : "text-rose-300"}`}>{faisa?.ecart != null ? money(faisa.ecart) : "—"}</div>
          </div>
        </div>
      </div>

      {/* PLAN DE DÉCAISSEMENT (DETTES) */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="refi-debts-table">
        <div className="flex items-center justify-between mb-3">
          <span className="font-semibold text-zinc-100 flex items-center gap-2"><Receipt size={18} className="text-rose-400" /> Plan de décaissement & remboursement des dettes</span>
          <div className="flex gap-2">
            {debts.length === 0 && <button onClick={seedDebts} data-testid="refi-seed-debts" className="text-xs bg-amber-600 hover:bg-amber-500 text-black font-semibold px-3 py-1.5 rounded-lg">Ajouter les 11 dettes initiales</button>}
            <button onClick={addDebt} data-testid="refi-add-debt" className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-3 py-1.5 rounded-lg flex items-center gap-1"><Plus size={13} /> Créancier</button>
          </div>
        </div>
        {debts.length === 0 ? <p className="text-xs text-zinc-500">Aucune dette. Ajoute les 11 dettes initiales pour démarrer.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-zinc-400 text-left">
                <th className="py-1 pr-2">Créancier</th><th className="py-1 pr-2 text-right">Solde déclaré</th>
                <th className="py-1 pr-2 text-right">Montant final</th><th className="py-1 pr-2">Statut</th>
                <th className="py-1 pr-2">Commentaires</th><th className="py-1"></th></tr></thead>
              <tbody>
                {debts.map((d) => (
                  <tr key={d.id} className="border-t border-zinc-800 align-top" data-testid={`refi-debt-${d.id}`}>
                    <td className="py-1.5 pr-2 text-zinc-200 max-w-[140px]">
                      <input value={d.creancier || ""} onChange={(e) => updateDebt(d.id, { creancier: e.target.value })} className="w-full bg-transparent border-b border-transparent hover:border-zinc-700 focus:border-amber-500 text-zinc-100 outline-none" />
                      <div className="text-[10px] text-zinc-500">{d.type}</div>
                    </td>
                    <td className="py-1.5 pr-2 text-right text-zinc-300">{money(d.solde_declare)}</td>
                    <td className="py-1.5 pr-2 text-right">
                      <input type="number" defaultValue={d.montant_remboursement_final ?? ""} onBlur={(e) => updateDebt(d.id, { montant_remboursement_final: e.target.value })} placeholder="à confirmer"
                        className="w-24 bg-zinc-950 border border-zinc-700 rounded px-1.5 py-0.5 text-right text-emerald-300" />
                    </td>
                    <td className="py-1.5 pr-2">
                      <select value={d.statut} onChange={(e) => updateDebt(d.id, { statut: e.target.value })} data-testid={`refi-debt-status-${d.id}`}
                        className="bg-zinc-950 border border-zinc-700 rounded px-1 py-1 text-zinc-200 text-[11px]">
                        {Object.entries(REFI_DEBT_STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </td>
                    <td className="py-1.5 pr-2">
                      <input defaultValue={d.commentaires || ""} onBlur={(e) => updateDebt(d.id, { commentaires: e.target.value })} placeholder="…" className="w-full bg-zinc-950 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-300" />
                    </td>
                    <td className="py-1.5"><button onClick={() => delDebt(d.id)} className="text-rose-400 hover:text-rose-300"><Trash size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t-2 border-zinc-700 font-semibold text-zinc-100">
                <td className="py-2">Total provisoire</td><td className="py-2 pr-2 text-right text-amber-300">{money(debtTotals.total_provisoire)}</td>
                <td className="py-2 pr-2 text-right text-emerald-300" data-testid="refi-total-confirme">{money(debtTotals.total_confirme)}</td>
                <td colSpan={3} className="py-2 text-xs text-zinc-500">Payé confirmé : {money(debtTotals.total_paye)}</td></tr></tfoot>
            </table>
          </div>
        )}
        <p className="text-[10px] text-zinc-500 mt-2">Ne coche « Payée » qu'après relevé final écrit + paiement + preuve. Vise « Solde zéro confirmé » pour clôturer.</p>
      </div>

      {/* CHECKLIST DOCUMENTS */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="refi-documents">
        <div className="font-semibold text-zinc-100 flex items-center gap-2 mb-3"><FolderSimple size={18} className="text-sky-400" /> Checklist de documents
          <span className="text-xs text-zinc-500">{docs.filter((x) => x.statut === "recu").length}/{docs.length} reçus</span></div>
        <div className="space-y-1.5">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1.5 text-xs" data-testid={`refi-doc-${d.id}`}>
              {d.statut === "recu" ? <CheckCircle size={16} weight="fill" className="text-emerald-400 flex-shrink-0" /> : <div className="w-4 h-4 rounded-full border border-zinc-600 flex-shrink-0" />}
              <span className="flex-1 text-zinc-200 truncate">{d.nom}{d.version > 1 && <span className="text-zinc-500"> · v{d.version}</span>}</span>
              <select value={d.statut} onChange={(e) => updateDoc(d.id, { statut: e.target.value })} data-testid={`refi-doc-status-${d.id}`}
                className="bg-zinc-900 border border-zinc-700 rounded px-1 py-0.5 text-[11px] text-zinc-200">
                {Object.entries(REFI_DOC_STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              {d.fichier_url && <a href={d.fichier_url} target="_blank" rel="noopener noreferrer" onClick={() => api.post(`/immeubles/refi/documents/${d.id}/log-download`).catch(() => {})} className="text-sky-400 hover:text-sky-300"><Eye size={15} /></a>}
              <label className="text-zinc-400 hover:text-amber-400 cursor-pointer" title="Téléverser">
                <UploadSimple size={15} />
                <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadDoc(d.id, e.target.files[0])} />
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* JOURNAL D'ACTIVITÉ */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="refi-activity">
        <div className="font-semibold text-zinc-100 flex items-center gap-2 mb-3"><ClockCounterClockwise size={18} className="text-zinc-400" /> Journal d'activité</div>
        {activity.length === 0 ? <p className="text-xs text-zinc-500">Aucune activité pour le moment (dépôts de documents et changements de statut des dettes s'afficheront ici).</p> : (
          <ol className="relative border-l border-zinc-800 ml-2 space-y-3">
            {activity.map((a) => (
              <li key={a.id} className="ml-4" data-testid={`refi-activity-${a.id}`}>
                <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-amber-500 border-2 border-zinc-900" />
                <div className="text-xs text-zinc-500">{(a.at || "").slice(0, 16).replace("T", " ")}</div>
                <div className="text-sm text-zinc-200">{a.detail}</div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

